/**
 * CINEMATIC SLIDER
 * Inspired by wearebrand.io
 * Vanilla JS — no external dependencies
 *
 * Features:
 * - Wheel event interception for navigation
 * - Independent per-layer mouse parallax
 * - Staggered per-layer entrance animations
 * - Cinematic vertical skew transitions
 * - Configurable cooldown between slides
 * - Touch/swipe support
 * - Prev/next buttons with disabled state at extremes
 */

(function () {
  'use strict';

  class CinematicSlider {
    constructor(el) {
      this.el = el;
      this.slides = Array.from(el.querySelectorAll('.cinematic-slide'));
      this.dots = Array.from(el.querySelectorAll('.cinematic-slider__dot'));
      this.btnPrev = el.querySelector('.cinematic-slider__nav-btn--prev');
      this.btnNext = el.querySelector('.cinematic-slider__nav-btn--next');

      // Config from data-attributes (set from Liquid)
      this.speed = parseInt(el.dataset.speed, 10) || 800;
      this.cooldown = parseInt(el.dataset.cooldown, 10) || 1000;
      this.parallaxEnabled = el.dataset.parallax !== 'false';
      // Use NaN check, not ||, so a user-set intensity of 0 is respected.
      const rawIntensity = parseFloat(el.dataset.parallaxIntensity);
      this.parallaxIntensity = isNaN(rawIntensity) ? 2 : rawIntensity;

      this.current = 0;
      this.total = this.slides.length;
      this.isAnimating = false;
      this.lastScroll = 0;
      this.mouseX = 0;
      this.mouseY = 0;
      this.rafId = null;
      this.wheelBuffer = 0;

      // Touch tracking
      this.touchStartX = 0;
      this.touchStartY = 0;

      // Bound handlers for clean removal
      this._onWheel = this._onWheel.bind(this);
      this._onMouseMove = this._onMouseMove.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);

      this.init();
    }

    init() {
      if (this.total === 0) return;

      this.setupCSS();
      this.bindWheel();
      this.bindTouch();
      this.bindButtons();
      this.bindMouse();
      this.updateNav();
      this.activateSlide(0, true);

      // Reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.parallaxEnabled = false;
      }
    }

    setupCSS() {
      this.el.style.setProperty('--slide-speed', this.speed + 'ms');
    }

    // ============ NAVIGATION ============

    /**
     * Transition to a specific slide.
     * Three independent animation tracks run simultaneously:
     *   1. Background JPEG: crossfade through white (slider bg) — no vertical motion
     *   2. PNG layers: each layer translates independently from a unique vh offset
     *   3. Text: fast fade-out on leaving, delayed fade-in on entering
     */
    goTo(index, direction) {
      if (this.isAnimating) return;
      if (index < 0 || index >= this.total) return;
      if (index === this.current) return;

      this.isAnimating = true;

      const SPEED = this.speed;
      const leaving = this.slides[this.current];
      const entering = this.slides[index];
      this.current = index;

      // ── LEAVING SLIDE ──
      this._fadeTextOut(leaving);
      this._animateLayersOut(leaving);

      const outBg = leaving.querySelector('.cinematic-slide__bg');
      if (outBg) {
        outBg.style.transition =
          'opacity ' + Math.round(SPEED * 0.5) + 'ms cubic-bezier(0.77, 0, 0.175, 1)';
        outBg.style.opacity = '0';
      }

      // ── ENTERING SLIDE ──
      entering.classList.add('is-active');

      // BG starts invisible; new bg fades in after the white-flash midpoint
      const inBg = entering.querySelector('.cinematic-slide__bg');
      if (inBg) {
        inBg.style.transition = 'none';
        inBg.style.opacity = '0';
        // force reflow so the transition re-attaches cleanly
        // eslint-disable-next-line no-unused-expressions
        inBg.offsetWidth;
        window.setTimeout(() => {
          inBg.style.transition =
            'opacity ' + Math.round(SPEED * 0.6) + 'ms cubic-bezier(0.77, 0, 0.175, 1)';
          inBg.style.opacity = '1';
        }, Math.round(SPEED * 0.4));
      }

      // PNG layers: each slides up from its own vh offset
      this._animateLayersIn(entering);

      // Text: delayed fade-in
      this._fadeTextIn(entering);

      // Cleanup leaving slide after the full transition window
      window.setTimeout(() => {
        leaving.classList.remove('is-active');

        // Reset leaving slide's inline styles so it's clean next time
        if (outBg) {
          outBg.style.transition = 'none';
          outBg.style.opacity = '';
        }
        leaving
          .querySelectorAll(
            '.cinematic-slide__headline, .cinematic-slide__description, .cinematic-slide__cta'
          )
          .forEach((el) => {
            el.style.transition = 'none';
            el.style.opacity = '';
            el.style.transform = '';
          });
        leaving.querySelectorAll('.cinematic-slide__layer').forEach((layer) => {
          const inner = layer.querySelector('.cinematic-slide__layer-inner') || layer;
          inner.style.transition = 'none';
          inner.style.opacity = '';
          inner.style.transform = '';
          layer._entranceDone = false;
          layer._parallax = { x: 0, y: 0 };
        });

        this.isAnimating = false;
        this.updateNav();
      }, SPEED + 300);

      this.updateDots();
    }

    goNext() {
      const now = Date.now();
      if (now - this.lastScroll < this.cooldown) return;
      if (this.current >= this.total - 1) return;
      this.lastScroll = now;
      this.goTo(this.current + 1, 'down');
    }

    goPrev() {
      const now = Date.now();
      if (now - this.lastScroll < this.cooldown) return;
      if (this.current <= 0) return;
      this.lastScroll = now;
      this.goTo(this.current - 1, 'up');
    }

    activateSlide(index, animate) {
      this.slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
      });
      if (animate) {
        this._animateLayersIn(this.slides[index]);
        this._fadeTextIn(this.slides[index]);
      }
      this.updateDots();
      this.updateNav();
    }

    // ============ LAYER + TEXT ANIMATION HELPERS ============

    /**
     * Animate PNG layers IN on an entering slide.
     * Each layer class has its own starting Y offset, delay and duration,
     * matching the staggered per-layer motion of the original reference.
     */
    _animateLayersIn(slide) {
      // Layer entrance timing table (vh-based translateY)
      const CFG = {
        'cinematic-slide__layer--ground':    { y: '60vh',  delay: 0,   dur: 700 },
        'cinematic-slide__layer--chars':     { y: '120vh', delay: 80,  dur: 950 },
        'cinematic-slide__layer--main':      { y: '100vh', delay: 0,   dur: 900 },
        'cinematic-slide__layer--fx':        { y: '80vh',  delay: 150, dur: 850 },
        'cinematic-slide__layer--shadow':    { y: '30vh',  delay: 200, dur: 600 },
        // Slide 2 (space scene) layers
        'cinematic-slide__layer--stars1':    { y: '20vh',  delay: 0,   dur: 1100 },
        'cinematic-slide__layer--stars2':    { y: '30vh',  delay: 50,  dur: 1000 },
        'cinematic-slide__layer--moon':      { y: '80vh',  delay: 100, dur: 900 },
        'cinematic-slide__layer--rocket':    { y: '120vh', delay: 200, dur: 1000 },
        'cinematic-slide__layer--character': { y: '60vh',  delay: 0,   dur: 800 },
        // Slide 3 (desert scene) layers
        'cinematic-slide__layer--sun':       { y: '40vh',  delay: 0,   dur: 1000 },
        'cinematic-slide__layer--scene':     { y: '60vh',  delay: 50,  dur: 900 },
        // Shared by slide 3 + slide 4
        'cinematic-slide__layer--cannabis':  { y: '100vh', delay: 150, dur: 900 },
        'cinematic-slide__layer--alien':     { y: '110vh', delay: 220, dur: 950 },
        // Slide 4 (lounge scene) layers
        'cinematic-slide__layer--smoke':     { y: '30vh',  delay: 100, dur: 1000 },
        'cinematic-slide__layer--props':     { y: '50vh',  delay: 120, dur: 900 },
        'cinematic-slide__layer--mouse':     { y: '80vh',  delay: 300, dur: 850 },
        'cinematic-slide__layer--text':      { y: '40vh',  delay: 400, dur: 900 }
      };
      const DEFAULT = { y: '100vh', delay: 0, dur: 900 };
      const EASING = 'cubic-bezier(0.23, 1, 0.32, 1)'; // power3.out — fast out, decelerate

      const layers = slide.querySelectorAll('.cinematic-slide__layer');
      layers.forEach((layer) => {
        layer._parallax = { x: 0, y: 0 };
        layer._entranceDone = false;

        const inner = layer.querySelector('.cinematic-slide__layer-inner') || layer;

        // Look up config by class name
        let cfg = DEFAULT;
        for (const cls in CFG) {
          if (layer.classList.contains(cls)) {
            cfg = CFG[cls];
            break;
          }
        }

        // Initial state
        inner.style.transition = 'none';
        inner.style.opacity = '0';
        inner.style.transform = 'translateY(' + cfg.y + ')';

        // Force reflow so the transition re-engages
        // eslint-disable-next-line no-unused-expressions
        inner.offsetWidth;

        window.setTimeout(() => {
          inner.style.transition =
            'transform ' + cfg.dur + 'ms ' + EASING + ' ' + cfg.delay + 'ms, ' +
            'opacity 400ms ease ' + cfg.delay + 'ms';
          inner.style.transform = 'translateY(0)';
          inner.style.opacity = '1';

          // Mark entrance complete so the parallax loop can take over
          window.setTimeout(() => {
            inner.style.transition = '';
            layer._entranceDone = true;
          }, cfg.delay + cfg.dur + 50);
        }, 50);
      });
    }

    /**
     * Fade PNG layers OUT on a leaving slide (fast 300ms fade).
     */
    _animateLayersOut(slide) {
      const layers = slide.querySelectorAll('.cinematic-slide__layer');
      layers.forEach((layer) => {
        layer._entranceDone = false; // stop the parallax loop from touching this layer
        const inner = layer.querySelector('.cinematic-slide__layer-inner') || layer;
        inner.style.transition = 'opacity 300ms ease';
        inner.style.opacity = '0';
      });
    }

    /**
     * Fade text OUT immediately on a leaving slide (200ms).
     */
    _fadeTextOut(slide) {
      const els = slide.querySelectorAll(
        '.cinematic-slide__headline, .cinematic-slide__description, .cinematic-slide__cta'
      );
      els.forEach((el) => {
        el.style.transition = 'opacity 200ms ease';
        el.style.opacity = '0';
      });
    }

    /**
     * Fade text IN on an entering slide.
     * Delayed 400ms so the PNG layers are already visible before the
     * copy appears, matching the original staggered choreography.
     */
    _fadeTextIn(slide) {
      const els = slide.querySelectorAll(
        '.cinematic-slide__headline, .cinematic-slide__description, .cinematic-slide__cta'
      );
      els.forEach((el, i) => {
        el.style.transition = 'none';
        el.style.opacity = '0';
        el.style.transform = 'translateY(20px)';
        // force reflow
        // eslint-disable-next-line no-unused-expressions
        el.offsetWidth;
        window.setTimeout(() => {
          el.style.transition = 'opacity 600ms ease, transform 600ms ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }, 400 + i * 100);
      });
    }

    // ============ PARALLAX ============

    bindMouse() {
      if (!this.parallaxEnabled) return;

      this.el.addEventListener('mousemove', this._onMouseMove, { passive: true });
      this.el.addEventListener('mouseleave', this._onMouseLeave);

      this.startParallaxLoop();
    }

    _onMouseMove(e) {
      const rect = this.el.getBoundingClientRect();
      this.mouseX = (e.clientX - rect.left - rect.width / 2) / rect.width;
      this.mouseY = (e.clientY - rect.top - rect.height / 2) / rect.height;
    }

    _onMouseLeave() {
      this.mouseX = 0;
      this.mouseY = 0;
    }

    startParallaxLoop() {
      const lerp = (a, b, t) => a + (b - a) * t;
      const LAG = 0.08; // smoothing factor (equivalent to lag:3 in original)

      const loop = () => {
        // Always run — the per-layer _entranceDone flag governs whether
        // an individual layer is currently eligible for parallax updates.
        const slide = this.slides[this.current];
        if (!slide) {
          this.rafId = window.requestAnimationFrame(loop);
          return;
        }

        const layers = slide.querySelectorAll('.cinematic-slide__layer');

        layers.forEach((layer) => {
          // Skip layers whose entrance animation hasn't finished yet
          if (!layer._entranceDone) return;

          const inner = layer.querySelector('.cinematic-slide__layer-inner') || layer;
          // Use NaN check, not ||, so a block-level factor of 0 is respected.
          const rawFactor = parseFloat(layer.dataset.parallaxFactor);
          const factor = isNaN(rawFactor) ? 1 : rawFactor;
          // Base movement amplitude — tuned so per-layer factor differences
          // are visually obvious at the default section intensity of 2.
          const targetX = this.mouseX * this.parallaxIntensity * factor * 60;
          const targetY = this.mouseY * this.parallaxIntensity * factor * 40;

          const curr = layer._parallax || { x: 0, y: 0 };
          curr.x = lerp(curr.x, targetX, LAG);
          curr.y = lerp(curr.y, targetY, LAG);
          layer._parallax = curr;

          // The outer .cinematic-slide__layer holds all static transforms
          // (centering, rotation). The inner wrapper only applies parallax offset.
          inner.style.transform = 'translate(' + curr.x + 'px, ' + curr.y + 'px)';
        });

        this.rafId = window.requestAnimationFrame(loop);
      };

      this.rafId = window.requestAnimationFrame(loop);
    }

    // ============ WHEEL ============

    bindWheel() {
      // In Shopify theme editor, do not hijack wheel events
      if (window.Shopify && window.Shopify.designMode) return;

      // passive: false is required to allow preventDefault
      this.el.addEventListener('wheel', this._onWheel, { passive: false });
    }

    _onWheel(e) {
      const rect = this.el.getBoundingClientRect();
      const inView = rect.top <= 0 && rect.bottom >= window.innerHeight;
      if (!inView) return;

      const atStart = this.current === 0 && e.deltaY < 0;
      const atEnd = this.current === this.total - 1 && e.deltaY > 0;

      if (!atStart && !atEnd) {
        e.preventDefault();
      } else {
        return;
      }

      const THRESHOLD = 50;
      this.wheelBuffer += e.deltaY;

      if (Math.abs(this.wheelBuffer) >= THRESHOLD) {
        if (this.wheelBuffer > 0) {
          this.goNext();
        } else {
          this.goPrev();
        }
        this.wheelBuffer = 0;
      }
    }

    // ============ TOUCH / SWIPE ============

    bindTouch() {
      this.el.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.el.addEventListener('touchend', this._onTouchEnd, { passive: true });
    }

    _onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    }

    _onTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      const dy = e.changedTouches[0].clientY - this.touchStartY;

      // Vertical swipe takes priority (cinematic up/down paradigm)
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 50) {
        if (dy < 0) this.goNext();
        else this.goPrev();
      }
    }

    // ============ BUTTONS ============

    bindButtons() {
      if (this.btnPrev) {
        this.btnPrev.addEventListener('click', () => this.goPrev());
      }
      if (this.btnNext) {
        this.btnNext.addEventListener('click', () => this.goNext());
      }
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.slideIndex, 10);
          if (!isNaN(index)) {
            const direction = index > this.current ? 'down' : 'up';
            this.goTo(index, direction);
          }
        });
      });
    }

    // ============ UI STATE ============

    updateNav() {
      if (this.btnPrev) this.btnPrev.disabled = this.current === 0;
      if (this.btnNext) this.btnNext.disabled = this.current === this.total - 1;
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.current);
      });
    }

    destroy() {
      if (this.rafId) window.cancelAnimationFrame(this.rafId);
      this.el.removeEventListener('wheel', this._onWheel);
      this.el.removeEventListener('mousemove', this._onMouseMove);
      this.el.removeEventListener('mouseleave', this._onMouseLeave);
      this.el.removeEventListener('touchstart', this._onTouchStart);
      this.el.removeEventListener('touchend', this._onTouchEnd);
    }
  }

  // ============ INITIALIZATION ============

  function initAll() {
    document.querySelectorAll('.cinematic-slider').forEach((el) => {
      if (el._cinematicSlider) return;
      el._cinematicSlider = new CinematicSlider(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor hooks — re-init when section is reloaded
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const slider = e.target.querySelector('.cinematic-slider');
      if (slider && !slider._cinematicSlider) {
        slider._cinematicSlider = new CinematicSlider(slider);
      }
    });

    document.addEventListener('shopify:section:unload', (e) => {
      const slider = e.target.querySelector('.cinematic-slider');
      if (slider && slider._cinematicSlider) {
        slider._cinematicSlider.destroy();
        slider._cinematicSlider = null;
      }
    });

    document.addEventListener('shopify:block:select', (e) => {
      const slide = e.target.closest('.cinematic-slide');
      const root = e.target.closest('.cinematic-slider');
      if (slide && root && root._cinematicSlider) {
        const index = parseInt(slide.dataset.slideIndex, 10);
        if (!isNaN(index)) {
          root._cinematicSlider.goTo(index);
        }
      }
    });
  }
})();
